import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../../app/hooks';
import config from '../../../config';
import { selectApiToken } from '../../auth/authSlice';
import { GPSCoordinates } from '../../commonUI/maps/types';
import { useDuckDb } from '../../duckDB/third-party-kit/hooks/useDuckDb';
import { CollectionPoint } from '../types';

// Use local DuckDB files
const SCANDATA_API_URL = config.api.cloudfrontScanDataUrl;

// We have a lot of other fields but it make sense to only return the ones we need
type ScanDataPoint = {
  id: string;
  exposure: number;
  left_camera_img_path: string;
  right_camera_img_path: string;
  gps: GPSCoordinates;
  block_id: string;
  camera_id: string;
  collection_id: string;
};

type UseGetCollectionPointsResult = {
  points: CollectionPoint[];
  isLoading: boolean;
  error: Error | null;
};

export const useGetCollectionPoints = (
  collectionId?: string
): UseGetCollectionPointsResult => {
  const access_token = useAppSelector(selectApiToken);
  const { db } = useDuckDb();
  const [points, setPoints] = useState<CollectionPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!collectionId || !db) {
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${SCANDATA_API_URL}collections/${collectionId}/merged_points.parquet`,
          {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(await response.text());
        }

        const parquetBuffer = await response.arrayBuffer();
        const conn = await db.connect();

        // Register and query the parquet file
        await db.registerFileBuffer(
          'points.parquet',
          new Uint8Array(parquetBuffer)
        );

        // Optimize the query to directly get the data we need
        const result = await conn.query(`
            SELECT
                id::VARCHAR as id,
                exposure::INTEGER as exposure,
                left_camera_img_path::VARCHAR as left_camera_img_path,
                right_camera_img_path::VARCHAR as right_camera_img_path,
                STRUCT_PACK(
                    latitude := gps.latitude::DOUBLE,
                    longitude := gps.longitude::DOUBLE
                ) as gps,
                 block_id::VARCHAR as block_id,
                 camera_id::VARCHAR as camera_id,
                 collection_id::VARCHAR as collection_id
            FROM read_parquet('points.parquet')
        `);

        // Convert to Points array more efficiently
        const fetchedPoints: CollectionPoint[] = result
          .toArray()
          .map<CollectionPoint>((row: ScanDataPoint) => ({
            id: String(row.id),
            gps: {
              latitude: row.gps.latitude,
              longitude: row.gps.longitude,
            },
            exposure: row.exposure,
            leftCameraImgPath: row.left_camera_img_path,
            rightCameraImgPath: row.right_camera_img_path,
            blockId: row.block_id,
            cameraId: row.camera_id,
            collectionId: row.collection_id,
          }));

        setPoints(fetchedPoints);

        // Clean up
        await conn.query('DROP TABLE IF EXISTS points_temp');
        conn.close();
      } catch (err) {
        console.error('Error fetching collection points:', err);
        setError(
          err instanceof Error ? err : new Error('Failed to fetch points')
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [collectionId, access_token, db]);

  // Memoize the points to prevent unnecessary re-renders
  const memoizedPoints = useMemo(() => points, [points]);

  return { points: memoizedPoints, isLoading, error };
};
